/**
 * Conditional formatting panel — colour cells in the grid (or bars in
 * charts) based on per-row column comparisons. Mirrors legacy
 * `_FormatNotes.cshtml`.
 *
 * Rules are stored on the indicator config as `format_rules: [...]` and
 * applied client-side by the chart components (no backend cost).
 */
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  type BuilderFormatRule,
  type DsidCatalogResponse,
  type FormatRuleOp,
  type IndicatorBuilderConfig,
} from '../../../lib/api/reporting';

interface ConditionalFormatPanelProps {
  config: IndicatorBuilderConfig;
  onChange: (next: IndicatorBuilderConfig) => void;
  catalog: DsidCatalogResponse | null;
}

const OPS: { value: FormatRuleOp; label: string }[] = [
  { value: 'eq',       label: '=' },
  { value: 'ne',       label: '≠' },
  { value: 'gt',       label: '>' },
  { value: 'gte',      label: '≥' },
  { value: 'lt',       label: '<' },
  { value: 'lte',      label: '≤' },
  { value: 'contains', label: 'contains' },
];

const DEFAULT_COLORS = ['#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6'];

export function ConditionalFormatPanel({ config, onChange, catalog }: ConditionalFormatPanelProps) {
  const dsid = config.x?.dsid ?? null;
  const datasetFields = catalog?.datasets.find((d) => d.dsid === dsid)?.fields ?? [];
  const rules: BuilderFormatRule[] = config.format_rules ?? [];

  const addRule = () => {
    const firstField = datasetFields[0];
    if (!firstField) return;
    onChange({
      ...config,
      format_rules: [
        ...rules,
        {
          column: firstField.label,
          op: 'gte',
          value: 0,
          color: DEFAULT_COLORS[rules.length % DEFAULT_COLORS.length],
        },
      ],
    });
  };

  const updateRule = (idx: number, patch: Partial<BuilderFormatRule>) => {
    onChange({
      ...config,
      format_rules: rules.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
    });
  };

  const removeRule = (idx: number) => {
    onChange({ ...config, format_rules: rules.filter((_, i) => i !== idx) });
  };

  if (dsid === null) {
    return (
      <div className="text-xs text-muted-foreground py-3">
        Pick an X axis first — conditional formatting operates on its columns.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-xs">Conditional formatting</Label>
        <Button type="button" variant="ghost" size="sm" className="h-7" onClick={addRule}>
          <Plus className="h-3.5 w-3.5 mr-1" />
          Add rule
        </Button>
      </div>
      {rules.length === 0 ? (
        <div className="text-xs text-muted-foreground italic">No rules configured.</div>
      ) : (
        <div className="space-y-1">
          {rules.map((r, idx) => (
            <div key={idx} className="grid grid-cols-[1fr_80px_110px_70px_auto] gap-1 items-center">
              <Select value={r.column} onValueChange={(v) => updateRule(idx, { column: v })}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {datasetFields.map((f) => (
                    <SelectItem key={f.field_id} value={f.label}>{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={r.op} onValueChange={(v) => updateRule(idx, { op: v as FormatRuleOp })}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {OPS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                className="h-8 text-xs"
                value={String(r.value)}
                onChange={(e) => updateRule(idx, { value: e.target.value })}
                placeholder="value"
              />
              <Input
                type="color"
                className="h-8 p-1 cursor-pointer"
                value={r.color}
                onChange={(e) => updateRule(idx, { color: e.target.value })}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-destructive"
                onClick={() => removeRule(idx)}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Evaluate a single rule against a row value. Returns the rule when matched,
 * else null. Callers iterate `format_rules` and pick the first/last match.
 */
export function matchFormatRule(
  rules: BuilderFormatRule[] | undefined,
  columnValue: unknown,
  columnLabel: string,
): BuilderFormatRule | null {
  if (!rules || rules.length === 0) return null;
  for (const r of rules) {
    if (r.column !== columnLabel) continue;
    const raw = columnValue;
    const num = typeof raw === 'number' ? raw : Number(raw);
    const rv = typeof r.value === 'number' ? r.value : Number(r.value);
    let matched = false;
    switch (r.op) {
      case 'eq':       matched = String(raw) === String(r.value); break;
      case 'ne':       matched = String(raw) !== String(r.value); break;
      case 'gt':       matched = Number.isFinite(num) && Number.isFinite(rv) && num >  rv; break;
      case 'gte':      matched = Number.isFinite(num) && Number.isFinite(rv) && num >= rv; break;
      case 'lt':       matched = Number.isFinite(num) && Number.isFinite(rv) && num <  rv; break;
      case 'lte':      matched = Number.isFinite(num) && Number.isFinite(rv) && num <= rv; break;
      case 'contains': matched = String(raw ?? '').toLowerCase().includes(String(r.value ?? '').toLowerCase()); break;
    }
    if (matched) return r;
  }
  return null;
}
